import { useParams, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '@g17eco/molecules/dashboard';
import { Breadcrumbs } from '@g17eco/molecules/breadcrumbs';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { getManageGroupsUrl } from './utils';
import NotAuthorised from '@routes/not-authorised';
import { Loader } from '@g17eco/atoms/loader';
import { useGetSubsidiaryGroupQuery } from '@api/subsidiary-groups';
import { skipToken } from '@reduxjs/toolkit/query';
import { SubsidiaryGroupForm } from './SubsidiaryGroupForm';
import { isRootOrg } from '@selectors/initiative';

interface RouteParams {
  initiativeId: string;
  groupId?: string;
}

export const CreateOrEditSubsidiaryGroupRoute = () => {
  const { initiativeId, groupId } = useParams<RouteParams>();
  const isEditMode = Boolean(groupId);
  const isRoot = useSelector(isRootOrg);

  const { data: group, isLoading, error } = useGetSubsidiaryGroupQuery(
    initiativeId && groupId ? { initiativeId, groupId } : skipToken
  );

  if (!initiativeId) {
    return <NotAuthorised />;
  }

  // Only root initiative can create/edit subsidiary groups
  if (!isRoot) {
    return <Redirect to={getManageGroupsUrl(initiativeId)} />;
  }

  const rootUrl = generateUrl(ROUTES.NAVIGATE_BY_MAP, { initiativeId });
  const breadcrumbs = [
    {
      label: 'Manage subsidiary groups',
      url: getManageGroupsUrl(initiativeId),
    },
    { label: isEditMode ? 'Edit group' : 'Create new group' },
  ];

  if (isEditMode && isLoading) {
    return <Loader />;
  }

  if (isEditMode && (error || !group)) {
    return (
      <Dashboard>
        <DashboardSection>
          <div className='text-center py-5 text-ThemeDangerMedium'>
            Failed to load subsidiary group. The group may have been deleted.
          </div>
        </DashboardSection>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <DashboardRow>
        <Breadcrumbs breadcrumbs={breadcrumbs} rootLabel='Organisation map' rootUrl={rootUrl} />
      </DashboardRow>
      <DashboardSectionTitle title={isEditMode ? 'Edit subsidiary group' : 'Create new group'} />
      <DashboardSection>
        <SubsidiaryGroupForm key={group?._id} initiativeId={initiativeId} group={group} />
      </DashboardSection>
    </Dashboard>
  );
};

